import { useEffect, useMemo, useRef } from 'react'
import Mention from '@tiptap/extension-mention'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { PluginKey, type EditorState } from '@tiptap/pm/state'

import { useOrganizationUsers } from '@/stores/organization.store'
import type { MentionUser } from './mention-list'
import { createMentionSuggestion } from './mention-suggestion'

/**
 * The @-mention suggestion's plugin key. One key serves every note editor
 * (each editor has its own state), so callers can ask whether the popup is open.
 */
const NOTE_MENTION_PLUGIN_KEY = new PluginKey('noteMention')

/**
 * The TipTap Mention extension for note editors, suggesting the organization's
 * users. The extension is created once; the suggestion reads the latest user
 * list when it runs, so it never goes stale as the list loads.
 */
export function useNoteMentionExtension() {
  const { users: orgUsers } = useOrganizationUsers()
  const mentionUsersRef = useRef<MentionUser[]>([])

  useEffect(() => {
    mentionUsersRef.current = orgUsers
      .map((ou) => ({
        id: ou.user?.id ?? '',
        firstName: ou.user?.firstName ?? '',
        lastName: ou.user?.lastName ?? '',
      }))
      .filter((u) => u.id)
  }, [orgUsers])

  return useMemo(
    () =>
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          ...createMentionSuggestion(() => mentionUsersRef.current),
          pluginKey: NOTE_MENTION_PLUGIN_KEY,
        },
      }),
    [],
  )
}

/** Whether the @-mention popup is open (Enter and Escape belong to it then). */
export function isNoteMentionSuggestionOpen(state: EditorState): boolean {
  return NOTE_MENTION_PLUGIN_KEY.getState(state)?.active === true
}

/** The distinct user ids mentioned in a note document, in document order. */
export function noteMentionedUserIds(doc: ProseMirrorNode): string[] {
  const ids: string[] = []
  doc.descendants((node) => {
    if (node.type.name === 'mention' && node.attrs.id) ids.push(String(node.attrs.id))
  })
  return [...new Set(ids)]
}
